import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiShield, FiUser, FiGlobe, FiSliders, FiTrash2, FiLock, FiLayout } from 'react-icons/fi';
import './Settings.css';
import ActivationSection from './sections/ActivationSection';
import ProfileSummarySection from './sections/ProfileSummarySection';
import LanguageSection from './sections/LanguageSection';
import OtherSettingsSection from './sections/OtherSettingsSection';
import CleanupSection from './sections/CleanupSection';
import ParentalControlsSection from './sections/ParentalControlsSection';
import InterfaceSection from './sections/InterfaceSection';
import PasswordModal from '../../components/PasswordModal/PasswordModal';
import { useAuth } from '../../contexts/AuthContext';
import { useParentalControls } from '../../contexts/ParentalControlsContext';

type SectionId = 'activation' | 'profile' | 'interface' | 'parental-controls' | 'language' | 'other' | 'cleanup';

interface SettingsProps {
  onBack: () => void;
}

const SECTIONS: { id: SectionId; icon: React.ReactNode; navKey: string; requiresProfile?: boolean }[] = [
  { id: 'activation',        icon: <FiShield size={16} />, navKey: 'settings.nav.activation' },
  { id: 'profile',           icon: <FiUser size={16} />,   navKey: 'settings.nav.profile' },
  { id: 'interface',         icon: <FiLayout size={16} />, navKey: 'settings.nav.interface' },
  { id: 'parental-controls', icon: <FiLock size={16} />,   navKey: 'settings.nav.parentalControls', requiresProfile: true },
  { id: 'language',          icon: <FiGlobe size={16} />,  navKey: 'settings.nav.language' },
  { id: 'other',             icon: <FiSliders size={16} />, navKey: 'settings.nav.other' },
  { id: 'cleanup',           icon: <FiTrash2 size={16} />,  navKey: 'settings.nav.cleanup' },
];

const Settings: React.FC<SettingsProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { authData } = useAuth();
  const { hasPassword, verifyPassword } = useParentalControls();
  const restoreSection = sessionStorage.getItem('ott_restore_settings_section') as SectionId | null;
  if (restoreSection) sessionStorage.removeItem('ott_restore_settings_section');
  const restoreIdx = restoreSection ? Math.max(0, SECTIONS.findIndex((s) => s.id === restoreSection)) : 0;
  const [activeSection, setActiveSection] = useState<SectionId>(restoreSection ?? 'activation');
  const [sidebarFocusIdx, setSidebarFocusIdx] = useState<number>(restoreIdx);
  const [focusZone, setFocusZone] = useState<'back' | 'sidebar' | 'content'>('sidebar');
  /** Unlocked for the current visit to the parental section only */
  const [parentalUnlocked, setParentalUnlocked] = useState(false);
  const [showParentalVerify, setShowParentalVerify] = useState(false);
  const [parentalVerifyError, setParentalVerifyError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Require password every time the user enters the parental section.
  useEffect(() => {
    if (activeSection !== 'parental-controls') {
      setParentalUnlocked(false);
    }
  }, [activeSection]);

  // Restore or deep-link: prompt verify instead of showing a blank panel.
  useEffect(() => {
    if (activeSection === 'parental-controls' && hasPassword() && !parentalUnlocked) {
      setShowParentalVerify(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Force focus on mount so keyboard navigation works immediately (autoFocus
  // is unreliable in SPAs where the element is inserted after the first paint).
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  // Global Back key handler — catches Samsung Tizen (keyCode 10009), LG webOS (461),
  // Escape and legacy Back keys regardless of which child element has focus.
  useEffect(() => {
    const isBackKey = (e: KeyboardEvent): boolean => {
      var k = e.key || '';
      var code = (e as any).keyCode || (e as any).which || 0;
      if (k === 'Escape' || k === 'Esc') return true;
      if (k === 'Back' || k === 'BrowserBack' || k === 'GoBack') return true;
      if (code === 10009 || code === 461) return true;
      return false;
    };
    const onGlobalBack = (e: KeyboardEvent) => {
      if (!isBackKey(e)) return;
      // A child modal owns Back while it is open (password/cleanup confirmation).
      if (document.querySelector('[aria-modal="true"]')) return;
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (isTyping) return;
      e.preventDefault();
      e.stopPropagation();
      onBack();
    };
    window.addEventListener('keydown', onGlobalBack, true);
    return () => window.removeEventListener('keydown', onGlobalBack, true);
  }, [onBack]);

  /** Returns all visible, non-disabled focusable elements inside the content panel. */
  const getContentFocusables = useCallback((): HTMLElement[] => {
    if (!contentRef.current) return [];
    return Array.from(
      contentRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [tabindex="0"]:not([disabled])'
      )
    ).filter((el) => (el as HTMLElement).offsetParent !== null);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const key = e.key;

      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      // Backspace when not typing also goes back (handled here since it's context-dependent)
      if (key === 'Backspace' && !isTyping) {
        e.preventDefault();
        e.stopPropagation();
        onBack();
        return;
      }

      if (focusZone === 'sidebar') {
        if (key === 'ArrowUp') {
          e.preventDefault();
          setSidebarFocusIdx((i) => Math.max(0, i - 1));
        } else if (key === 'ArrowDown') {
          e.preventDefault();
          setSidebarFocusIdx((i) => Math.min(SECTIONS.length - 1, i + 1));
        } else if (key === 'Enter') {
          e.preventDefault();
          const target = SECTIONS[sidebarFocusIdx];
          if (target.requiresProfile && !authData) return;
          if (target.id === 'parental-controls' && hasPassword() && !parentalUnlocked) {
            setShowParentalVerify(true);
            return;
          }
          setActiveSection(target.id);
          setFocusZone('content');
          setTimeout(() => { getContentFocusables()[0]?.focus(); }, 0);
        } else if (key === 'ArrowRight') {
          e.preventDefault();
          setFocusZone('content');
        } else if (key === 'ArrowLeft') {
          e.preventDefault();
          setFocusZone('back');
        }
      } else if (focusZone === 'back') {
        if (key === 'Enter') {
          e.preventDefault();
          onBack();
        } else if (key === 'ArrowRight') {
          e.preventDefault();
          setFocusZone('sidebar');
        }
      } else if (focusZone === 'content') {
        if (key === 'ArrowLeft') {
          e.preventDefault();
          setFocusZone('sidebar');
          // Return DOM focus to the container so sidebar keydown handling works
          // and the content's focused element (e.g. dropdown button) can't intercept Enter.
          containerRef.current?.focus();
        } else if (key === 'ArrowDown' || key === 'ArrowUp') {
          // Navigate between focusable elements inside the content panel.
          // Sections that handle their own navigation (open dropdowns, etc.)
          // call e.stopPropagation() so those keys never reach here.
          const focusables = getContentFocusables();
          if (focusables.length === 0) return;
          const currentIdx = focusables.indexOf(document.activeElement as HTMLElement);
          if (key === 'ArrowDown') {
            e.preventDefault();
            const next = focusables[Math.min(focusables.length - 1, currentIdx + 1)];
            next?.focus();
          } else {
            e.preventDefault();
            if (currentIdx <= 0) {
              // Already on first focusable — go back to sidebar
              setFocusZone('sidebar');
              containerRef.current?.focus();
            } else {
              focusables[currentIdx - 1]?.focus();
            }
          }
        }
      }
    },
    [authData, focusZone, sidebarFocusIdx, onBack, getContentFocusables]
  );

  return (
    <div
      ref={containerRef}
      className="settings"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="settings__backdrop" />

      {/* Header */}
      <div className="settings__header">
        <button
          className={`settings__back-btn${focusZone === 'back' ? ' settings__back-btn--focused' : ''}`}
          onClick={onBack}
          tabIndex={-1}
          aria-label={t('common.back')}
        >
          ‹
        </button>
        <h1 className="settings__title">{t('settings.title')}</h1>
      </div>

      {/* Body */}
      <div className="settings__body">
        {/* Sidebar */}
        <nav className="settings__sidebar" aria-label={t('settings.title')}>
          {SECTIONS.map((s, idx) => (
            <button
              key={s.id}
              disabled={s.requiresProfile && !authData}
              className={[
                'settings__sidebar-btn',
                s.requiresProfile && !authData ? 'settings__sidebar-btn--disabled' : '',
                activeSection === s.id ? 'settings__sidebar-btn--active' : '',
                focusZone === 'sidebar' && sidebarFocusIdx === idx
                  ? 'settings__sidebar-btn--focused'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                if (s.requiresProfile && !authData) return;
                if (s.id === 'parental-controls' && hasPassword() && !parentalUnlocked) {
                  setShowParentalVerify(true);
                  return;
                }
                setActiveSection(s.id);
                setSidebarFocusIdx(idx);
                setFocusZone('content');
              }}
              tabIndex={-1}
            >
              <div className="settings__sidebar-icon-circle">{s.icon}</div>
              {t(s.navKey)}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div ref={contentRef} className="settings__content">
          {activeSection === 'activation' && (
            <ActivationSection focused={focusZone === 'content'} />
          )}
          {activeSection === 'profile' && (
            <ProfileSummarySection focused={focusZone === 'content'} />
          )}
          {activeSection === 'interface' && (
            <InterfaceSection focused={focusZone === 'content'} />
          )}
          {activeSection === 'language' && (
            <LanguageSection focused={focusZone === 'content'} />
          )}
          {activeSection === 'other' && (
            <OtherSettingsSection focused={focusZone === 'content'} />
          )}
          {activeSection === 'cleanup' && (
            <CleanupSection focused={focusZone === 'content'} />
          )}
          {activeSection === 'parental-controls' && (
            <ParentalControlsSection
              focused={focusZone === 'content'}
              interactive={parentalUnlocked || !hasPassword()}
              onUnlocked={() => setParentalUnlocked(true)}
            />
          )}
        </div>
      </div>

      {showParentalVerify && (
        <PasswordModal
          mode="verify"
          error={parentalVerifyError}
          onConfirm={(pass) => {
            if (!verifyPassword(pass)) {
              setParentalVerifyError(t('parental.wrongPassword'));
              return;
            }
            setParentalVerifyError('');
            setShowParentalVerify(false);
            setParentalUnlocked(true);
            setActiveSection('parental-controls');
            setFocusZone('content');
          }}
          onCancel={() => {
            setShowParentalVerify(false);
            setParentalVerifyError('');
            // Restore focus to the Settings container so keyboard nav continues
            // from the sidebar (focusZone is still 'sidebar').
            setTimeout(() => containerRef.current?.focus(), 0);
          }}
        />
      )}
    </div>
  );
};

export default Settings;
